import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  Keyboard, Command, Search, Plus, ArrowUp, ArrowDown, 
  Bold, Italic, Strikethrough, Heading1, List, CheckSquare,
  Code, Quote, Save, Trash, Archive, Pin, Star, Share2
} from 'lucide-react';

const SHORTCUTS = [
  {
    category: 'Navigation',
    items: [
      { keys: ['Ctrl', 'K'], description: 'Open command palette / search', icon: Search },
      { keys: ['Ctrl', 'N'], description: 'Create new note', icon: Plus },
      { keys: ['↑', '↓'], description: 'Navigate between notes', icon: ArrowUp },
      { keys: ['Escape'], description: 'Close dialogs / deselect', icon: null },
    ],
  },
  {
    category: 'Editor - Text Formatting',
    items: [
      { keys: ['Ctrl', 'B'], description: 'Bold text', icon: Bold },
      { keys: ['Ctrl', 'I'], description: 'Italic text', icon: Italic },
      { keys: ['Ctrl', 'Shift', 'S'], description: 'Strikethrough', icon: Strikethrough },
      { keys: ['Ctrl', 'Shift', 'H'], description: 'Highlight text', icon: null },
    ],
  },
  {
    category: 'Editor - Blocks',
    items: [
      { keys: ['#', 'Space'], description: 'Heading 1', icon: Heading1 },
      { keys: ['##', 'Space'], description: 'Heading 2', icon: null },
      { keys: ['###', 'Space'], description: 'Heading 3', icon: null },
      { keys: ['-', 'Space'], description: 'Bullet list', icon: List },
      { keys: ['1.', 'Space'], description: 'Numbered list', icon: null },
      { keys: ['[]', 'Space'], description: 'Task list', icon: CheckSquare },
      { keys: ['```'], description: 'Code block', icon: Code },
      { keys: ['>', 'Space'], description: 'Blockquote', icon: Quote },
    ],
  },
  {
    category: 'Editor - Actions',
    items: [
      { keys: ['Ctrl', 'Z'], description: 'Undo', icon: null },
      { keys: ['Ctrl', 'Shift', 'Z'], description: 'Redo', icon: null },
      { keys: ['Ctrl', 'S'], description: 'Force save', icon: Save },
    ],
  },
  {
    category: 'Note Actions',
    items: [
      { keys: ['Ctrl', 'Shift', 'P'], description: 'Toggle pin', icon: Pin },
      { keys: ['Ctrl', 'Shift', 'F'], description: 'Toggle favorite', icon: Star },
      { keys: ['Ctrl', 'Shift', 'A'], description: 'Archive note', icon: Archive },
      { keys: ['Ctrl', 'Shift', 'D'], description: 'Delete note', icon: Trash },
      { keys: ['Ctrl', 'Shift', 'L'], description: 'Share note', icon: Share2 },
      { keys: ['Ctrl', 'Shift', 'E'], description: 'Export as PDF', icon: null },
    ],
  },
];

export default function KeyboardShortcuts() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Open on ? or F1
      if ((e.key === '?' && !e.ctrlKey && !e.metaKey) || e.key === 'F1') {
        // Don't trigger if typing in an input
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
          if (e.key === 'F1') {
            e.preventDefault();
            setOpen(true);
          }
          return;
        }
        e.preventDefault();
        setOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden" data-testid="keyboard-shortcuts-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="w-5 h-5" />
            Keyboard Shortcuts
          </DialogTitle>
          <DialogDescription className="sr-only">
            List of all available keyboard shortcuts for navigation, text formatting, blocks, and actions.
          </DialogDescription>
        </DialogHeader>
        
        <div className="overflow-y-auto max-h-[65vh] pr-2 -mr-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {SHORTCUTS.map((category) => (
              <div key={category.category} className="space-y-2">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {category.category}
                </h3>
                <div className="space-y-1">
                  {category.items.map((shortcut) => (
                    <div 
                      key={shortcut.description} 
                      className="flex items-center justify-between py-1.5 px-2 rounded-sm hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-2 text-sm">
                        {shortcut.icon && <shortcut.icon className="w-3.5 h-3.5 text-muted-foreground" />}
                        <span>{shortcut.description}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {shortcut.keys.map((key, keyIdx) => (
                          <span key={key}>
                            <kbd className="px-1.5 py-0.5 text-xs font-mono bg-secondary border border-border rounded-sm shadow-sm">
                              {key}
                            </kbd>
                            {keyIdx < shortcut.keys.length - 1 && (
                              <span className="mx-0.5 text-muted-foreground text-xs">+</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-4 border-t border-border mt-4">
          <p className="text-xs text-muted-foreground text-center">
            Press <kbd className="px-1 py-0.5 text-xs font-mono bg-secondary border border-border rounded-sm">?</kbd> or{' '}
            <kbd className="px-1 py-0.5 text-xs font-mono bg-secondary border border-border rounded-sm">F1</kbd> anytime to show this help
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
